import type { MachineId, TokenSpec } from '../../shared/contract.js';
import { ctxFor } from '../modifier.js';
import type { TokenRuntime } from '../token.js';
import { v3, type Vec3 } from '../vec.js';
import { Cabinet, type CabinetHost, type FieldSolid, type TagTable } from './base.js';

/**
 * FLIPPER — the Saalchef's table.
 *
 * machines.md reserves pinball for the bosses: "each boss is a multiphase pinball table
 * that plays *back*: it has its own Beutel, its own modifiers, and it spends GLÜCK against
 * you." This is that table. The personality lives in `core/run.ts` (phases, targets,
 * trophy); everything physical lives here, and it is an ordinary `Cabinet` — same base
 * class, same four verbs, same structural limit, same Hausregeln board. That is the whole
 * reason the boss law holds without special cases: your [Gummi] ball plays differently
 * from your [Schwer] ball on the Saalchef's table for free, because it is a real table
 * with real tokens on it and nobody wrote a line of boss-specific tag code.
 *
 * **The rake.** A real pinball table is inclined about 6–8°, and the component of gravity
 * along it is `g·sin θ`. This table is modelled as a vertical board — like the pachinko
 * field, so it lives in the same diorama and reuses the same "recess proud of the face"
 * construction the ART-BIBLE demands — which means the rake has to come back as a
 * per-body gravity scale. That is physics, not a difficulty knob: at full gravity the
 * ball reaches the flippers at roughly nineteen units a second and no human, and no bot,
 * can play it.
 *
 * **Setzen is the flip.** See the contract's `SetzenPayload`: adding a sixth verb would
 * have forced "flip" to mean something on the coin pusher. The plunger, the left bat and
 * the right bat are all commitments, and commitments are what Setzen logs.
 */

export interface FlipperSetzen {
  flip: 'left' | 'right' | 'plunger';
  /** plunger draw, 0..1; ignored by the bats (a bat is all the way or not at all) */
  power: number;
  spec: TokenSpec;
}

/** sin(8°) — the rake of a real table, as a gravity multiplier. */
export const RAKE_GRAVITY = 0.139;

const TABLE = {
  halfWidth: 6.0,
  height: 22,
  halfDepth: 0.8,
  /** the plunger lane runs up the right-hand edge */
  laneX: 5.3,
  laneInner: 4.6,
  launchPos: v3(5.3, 2.0, 0),
  /** below this, between the outlane walls, the ball is gone */
  drainY: 1.4,
  flipperPivotY: 4.2,
  flipperPivotX: 2.15,
  flipperHalfLen: 1.55,
  flipperHalfThick: 0.28,
  /** bat angles, radians from horizontal: resting droop and the flipped-up position */
  flipperRest: -0.46,
  flipperUp: 0.62,
  /** how long a bat takes to sweep, in seconds — a real flipper is ~50 ms */
  flipTime: 0.055,
  /** and how long it stays up before dropping back */
  flipHold: 0.16,
} as const;

interface Bumper { pos: Vec3; r: number; value: number }
interface DropTarget { pos: Vec3; h: Vec3; down: boolean; value: number }

export class Flipper extends Cabinet<FlipperSetzen> {
  readonly id: MachineId = 'flipper';
  readonly limitLabel = 'Tischmaximum — darüber hebt sich die Scheibe und der Saal zahlt alles.';
  readonly breakMode = 'glass' as const;

  /**
   * Declared odds mappings. Short, because a pinball table is almost pure physics — which
   * is the same proof the pachinko field gives. Only the two questions physics cannot
   * answer are declared: how wide the losing channel is, and whether a ball can be
   * cradled on the bat.
   */
  readonly tagTable: TagTable = {
    Kleber: {
      effect: 'cradle',
      note: 'Diese Marke bleibt auf dem Schläger liegen — Sie dürfen in Ruhe zielen.',
    },
    Gluecks: {
      effect: 'narrowOutlane',
      note: 'Die Verlustrinnen verengen sich für diese Marke um ein Fünftel.',
    },
  };

  private solids: FieldSolid[] = [];
  private bumpers: Bumper[] = [];
  private targets: DropTarget[] = [];
  private targetBodies: number[] = [];
  /** bat state: -1 = resting, otherwise seconds since the flip began */
  private flipT: [number, number] = [-1, -1];
  private batBodies: [number, number] = [-1, -1];
  private scaled = new Set<number>();
  private balls = 0;
  private drained = 0;
  private targetsCleared = 0;
  /** the Saalchef may deny exactly one target hit per charge, and it is telegraphed */
  private denyArmed = false;
  private denyTelegraphedAt = -1;
  private cradled = new Map<number, number>();
  /** (tokenId, bumperIndex) -> tick of last kick. Instance state, never module state. */
  private readonly bumperCooldown = new Map<number, number>();
  /**
   * Which tokens on this table are the Saalchef's rather than yours.
   *
   * This distinction is load-bearing and its absence was a genuine soft-lock. His coins
   * are obstacles: [Kleber] ones weld themselves to the field and stay for the whole
   * fight. Counting them as "balls in play" meant that after three of them the player
   * could no longer put a ball on the table at all — measured: three launches in
   * forty-five seconds, one score, nothing banked. The Saalchef was winning by clutter,
   * which is not a mechanic, it is a deadlock.
   */
  private readonly houseTokens = new Set<number>();

  build(): void {
    const phys = this.host.phys;
    const W = TABLE.halfWidth;
    const H = TABLE.height;
    const D = TABLE.halfDepth;

    // ---- shell. Even a ghost cannot leave the room through this (see LAYER.SHELL).
    phys.addStatic({ kind: 'box', hx: W + 0.2, hy: 0.2, hz: D + 0.2, pos: this.w(v3(0, -0.4, 0)) }, 'shell', 0.5, 0.1);
    phys.addStatic({ kind: 'box', hx: 0.2, hy: H / 2, hz: D, pos: this.w(v3(-W - 0.2, H / 2, 0)) }, 'shell', 0.1, 0.5);
    phys.addStatic({ kind: 'box', hx: 0.2, hy: H / 2, hz: D, pos: this.w(v3(W + 0.2, H / 2, 0)) }, 'shell', 0.1, 0.5);
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: H / 2, hz: 0.2, pos: this.w(v3(0, H / 2, -D - 0.2)) }, 'shell', 0.2, 0.2);
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: H / 2, hz: 0.2, pos: this.w(v3(0, H / 2, D + 0.2)) }, 'shell', 0.2, 0.2);
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: 0.2, hz: D + 0.4, pos: this.w(v3(0, H + 0.2, 0)) }, 'shell', 0.2, 0.2);

    // ---- the plunger lane: a wall separating it from the field, stopping short of the
    // top so the ball is spat leftward into play and cannot come back down the lane.
    phys.addStatic(
      { kind: 'box', hx: 0.16, hy: 7.2, hz: D, pos: this.w(v3(TABLE.laneInner, 7.6, 0)) },
      'shell', 0.05, 0.25,
    );
    /**
     * THE RETURN. A plunger lane is useless without something at the top that turns "up"
     * into "left and down"; otherwise the ball rides to its apex and falls straight back
     * down the lane it came from, which is exactly what this table did for its first three
     * builds. Traced rather than reasoned about: the ball climbed to y=16.1 at x=5.3 and
     * came back down the same line, never once entering the field, so the whole fight was
     * a plunger and a wait.
     *
     * The first attempt was three horizontal chords across the top. That was the wrong
     * shape — a flat plate stops a rising ball, it does not deflect it, and the top chord
     * did not even reach x=5.3.
     *
     * The right shape with the primitives core owns (box / ball / cylinder, all
     * axis-aligned) is a **sphere**: the rising ball glances off its lower-left quadrant
     * and leaves at roughly forty-five degrees down and to the left. The second and third
     * spheres keep it tracking across the top instead of dropping straight back into the
     * lane mouth, and they are where a real table would have its bonus lanes.
     */
    /*
     * The deflector's position is solved, not eyeballed. The lane channel runs at about
     * x = 5.4; for the ball to leave at forty-five degrees the contact normal must be
     * (-1,-1)/√2, which puts the sphere's centre up and to the right of the contact point
     * by (r + ballRadius)/√2 in both axes. Hence x = 6.9 for r = 1.6.
     *
     * The previous attempt sat at x = 6.0 with r = 1.9, which sounds similar and is not:
     * that sphere's lowest point was directly over the lane, so the ball met its *bottom*,
     * where the normal is straight down, and was returned neatly to the plunger. It looked
     * for all the world like the deflector was missing.
     */
    for (const [x, y, r] of [[6.9, 17.0, 1.6], [2.6, 19.2, 1.3], [-1.8, 19.6, 1.3]] as const) {
      const pos = this.w(v3(x, y, 0));
      phys.addStatic({ kind: 'ball', radius: r, pos }, 'shell', 0.05, 0.5);
      this.solids.push({ kind: 'ball', pos, r });
    }

    // ---- bumpers (Pilze). High restitution: these are the things that give the ball
    // energy back, and without them a raked table just drains.
    const bumperSpec: [number, number, number][] = [
      [-2.6, 12.6, 6], [0.4, 14.6, 6], [2.7, 11.9, 6], [-0.9, 10.4, 4],
    ];
    for (const [x, y, value] of bumperSpec) {
      const pos = this.w(v3(x, y, 0));
      phys.addStatic({ kind: 'ball', radius: 0.78, pos }, 'field', 0.04, 1.02);
      this.solids.push({ kind: 'ball', pos, r: 0.78 });
      this.bumpers.push({ pos, r: 0.78, value });
    }

    // ---- drop targets: the boss's phase progress, as objects you knock over
    /**
     * The bank sits at y = 15.4, not 17.1, and the number comes from a trace rather than
     * from taste: with the deflector feeding the ball across the top and down the left,
     * the ball spends nearly all of its life between y = 12 and y = 16, in and around the
     * bumper cluster. Targets parked above that band were passed once on the way in and
     * then never touched again — one score in forty-five seconds. Put the targets where
     * the ball actually is.
     */
    const targetXs = [-4.1, -2.6, -1.1, 0.4, 1.9];
    for (const x of targetXs) {
      const pos = this.w(v3(x, 15.4, 0));
      const h = v3(0.55, 0.42, D * 0.8);
      const body = phys.addStatic({ kind: 'box', hx: h.x, hy: h.y, hz: h.z, pos }, 'field', 0.2, 0.55);
      this.targets.push({ pos, h, down: false, value: 12 });
      this.targetBodies.push(body);
      this.solids.push({ kind: 'box', pos, h });
    }

    // ---- slingshots: the angled kickers above each bat
    for (const s of [-1, 1]) {
      const pos = this.w(v3(s * 4.05, 6.6, 0));
      phys.addStatic({ kind: 'box', hx: 0.22, hy: 1.5, hz: D, pos }, 'field', 0.05, 0.92);
      this.solids.push({ kind: 'box', pos, h: v3(0.22, 1.5, D) });
    }

    // ---- outlane walls: the funnel that makes the drain a real threat
    for (const s of [-1, 1]) {
      const pos = this.w(v3(s * 4.5, 2.6, 0));
      phys.addStatic({ kind: 'box', hx: 0.18, hy: 2.4, hz: D, pos }, 'shell', 0.1, 0.2);
      this.solids.push({ kind: 'box', pos, h: v3(0.18, 2.4, D) });
    }

    // ---- the bats themselves, kinematic and lively
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? -1 : 1;
      const pivot = v3(s * TABLE.flipperPivotX, TABLE.flipperPivotY, 0);
      const h = this.batPose(i, TABLE.flipperRest, pivot);
      this.batBodies[i] = phys.addKinematicBoxPose(
        TABLE.flipperHalfLen, TABLE.flipperHalfThick, TABLE.halfDepth * 0.7,
        this.w(h.pos), 0.22, 0.42,
      );
    }
  }

  /** Where the bat's centre sits, and how it is turned, for a given angle. */
  private batPose(i: number, angle: number, pivot: Vec3): { pos: Vec3; rot: [number, number, number, number] } {
    const s = i === 0 ? 1 : -1;               // left bat points right, right bat points left
    const a = angle * s;
    const cx = pivot.x + Math.cos(a) * TABLE.flipperHalfLen * s;
    const cy = pivot.y + Math.sin(a) * TABLE.flipperHalfLen;
    // rotation about the board normal (+z)
    const half = a / 2;
    return { pos: v3(cx, cy, 0), rot: [0, 0, Math.sin(half), Math.cos(half)] };
  }

  fieldSolids(): readonly FieldSolid[] {
    return this.solids;
  }

  /** Verb 1: Setzen — the plunger, or a bat. */
  setzen(p: FlipperSetzen): void {
    if (this.broken) return;
    if (this.host.tick < this.lockoutUntil) return;

    if (p.flip === 'plunger') {
      const power = clamp(p.power, 0, 1);
      /**
       * A plunger is a spring: all of its energy goes straight up the lane. The speed is
       * derived, not guessed — under the rake the ball climbs `v²/(2·g·RAKE)`, so from the
       * launch point at y=2 a full-power shot must reach about the top of a 22-unit table
       * and no further:  v = sqrt(2 · 9.81 · 0.139 · 19) ≈ 7.2.
       *
       * The first version used the pachinko launcher's numbers (15–28) and the table was
       * unplayable in a way that looked like a geometry bug: the ball's apex worked out at
       * **82 units** on a 22-unit table, so every shot slammed into the top glass at
       * speed, rattled out of the field, and left without ever touching a drop target.
       *
       * The correction then overshot the other way — 3.6–7.6 put the apex at 14 units,
       * *below the 14.8-unit lane wall*, so a mid-power plunge never left the lane at all.
       * The floor is therefore set by the lane, not by taste: the weakest possible plunge
       * must still clear it, and the strongest should just kiss the top glass.
       */
      const speed = 6.2 + 2.2 * power;
      const tok = this.host.spawnToken(p.spec, this.id, this.w(TABLE.launchPos), v3(0, speed, 0));
      this.applyRake(tok);
      this.balls++;
      this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
      this.host.pipe.fire(
        'onDrop',
        { ...ctxFor(this.id, this.host.tick, tok), api: this.host.api },
        { token: tok.id },
      );
      return;
    }

    const i = p.flip === 'left' ? 0 : 1;
    if (this.flipT[i] >= 0) return;          // already swinging; a bat is not a machine gun
    this.flipT[i] = 0;
    // releasing a cradled ball is the whole point of catching one
    this.releaseCradle(i);
  }

  /**
   * The rake, applied per token as it enters the table (see RAKE_GRAVITY). Tokens that
   * leave — Der Deckel can genuinely carry one into the neighbouring cabinet — get their
   * weight back in `tickMachine`, or a coin would fall through the rest of the arcade in
   * slow motion for the rest of the run.
   */
  private applyRake(t: TokenRuntime): void {
    if (t.body < 0 || this.scaled.has(t.id)) return;
    this.host.phys.setGravityScale(t.body, RAKE_GRAVITY);
    this.scaled.add(t.id);
  }

  private unrake(t: TokenRuntime): void {
    if (t.body >= 0 && this.scaled.has(t.id)) this.host.phys.setGravityScale(t.body, 1);
    this.scaled.delete(t.id);
  }

  protected escalate(level: number): void {
    // Nachlegen on a pinball table is multiball, and multiball is also how you burst it.
    const spec = this.host.drawFromBeutel('flipper:multiball');
    const tok = this.host.spawnToken(spec, this.id, this.w(v3(-2 + level * 0.4, 19.5, 0)), v3(-2, 0, 0));
    this.applyRake(tok);
    this.balls++;
    this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
  }

  /**
   * The Saalchef's own bag, put on the table as a physical obstacle.
   *
   * Capped at HOUSE_MAX simultaneously, oldest cleared first. Uncapped, his [Kleber]
   * coins silt the table up until nothing can move — his pressure has to be a hazard you
   * play around, not a wall that ends the game.
   */
  dropHausmuenze(spec: TokenSpec): number {
    while (this.houseTokens.size >= HOUSE_MAX) {
      const oldest = this.houseTokens.values().next().value as number | undefined;
      if (oldest === undefined) break;
      this.houseTokens.delete(oldest);
      this.host.removeToken(oldest, 'lost');
    }
    const tok = this.host.spawnToken(spec, this.id, this.w(v3(-3.4, 19.0, 0)), v3(1.2, 0, 0));
    this.applyRake(tok);
    this.houseTokens.add(tok.id);
    this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
    return tok.id;
  }

  /** Balls that are YOURS — the number that decides whether you may plunge again. */
  playerBalls(): number {
    return this.host.tokensIn(this.id)
      .filter((t) => t.body >= 0 && !this.houseTokens.has(t.id)).length;
  }

  /**
   * The Saalchef spends GLÜCK against you. It is armed a beat BEFORE it bites and the
   * arming is an event, because a boss that silently cancels your shot is not a boss, it
   * is a bug with a name. house-and-heat.md's law: no invisible rubber-banding, ever.
   */
  armDeny(): void {
    this.denyArmed = true;
    this.denyTelegraphedAt = this.host.tick;
  }

  denyPending(): boolean {
    return this.denyArmed;
  }

  targetsDown(): number {
    let n = 0;
    for (const t of this.targets) if (t.down) n++;
    return n;
  }

  /** How many full banks of targets have been cleared — the boss's phase currency. */
  cleared(): number {
    return this.targetsCleared;
  }

  resetTargets(): void {
    for (const t of this.targets) t.down = false;
  }

  ballsInPlay(): number {
    return this.host.tokensIn(this.id).filter((t) => t.body >= 0).length;
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken) return;

    this.tickBats(dt);

    for (const t of this.host.tokensIn(this.id)) {
      if (t.body < 0) continue;
      this.applyRake(t);
      if (t.stuck) continue;
      const p = this.host.api.position(t.id);
      if (!p) continue;
      const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };

      // ---- gone from the table entirely
      if (local.y < -1.5 || Math.abs(local.x) > TABLE.halfWidth + 1.5) {
        this.unrake(t);
        this.host.escaped(t, this, this.escapeMode(t), p);
        continue;
      }

      // ---- the drain
      if (local.y <= TABLE.drainY) {
        this.drain(t, local);
        continue;
      }

      // ---- bumpers and targets are resolved analytically rather than from contact
      // events, because a contact event tells you two colliders touched and not which
      // scoring object it was. The geometry is ours; we may as well ask it.
      this.checkBumpers(t, local, p);
      this.checkTargets(t, local, p);
    }

    this.checkLimit();
  }

  /**
   * The bats. A flipper is a *sweep*: it must arrive at the ball with speed, or it merely
   * leans on it. Position-based kinematics gives the ball momentum through the sweep
   * itself, which is why `flipTime` is 55 ms and not "instant" — an instant bat teleports
   * through the ball and imparts nothing, which is the classic way a hand-rolled pinball
   * table ends up feeling dead.
   */
  private tickBats(dt: number): void {
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? -1 : 1;
      const pivot = v3(s * TABLE.flipperPivotX, TABLE.flipperPivotY, 0);
      let angle: number = TABLE.flipperRest;
      if (this.flipT[i] >= 0) {
        this.flipT[i] += dt;
        const t = this.flipT[i];
        if (t < TABLE.flipTime) {
          angle = lerp(TABLE.flipperRest, TABLE.flipperUp, t / TABLE.flipTime);
        } else if (t < TABLE.flipTime + TABLE.flipHold) {
          angle = TABLE.flipperUp;
        } else {
          const u = (t - TABLE.flipTime - TABLE.flipHold) / (TABLE.flipTime * 2.2);
          if (u >= 1) { this.flipT[i] = -1; angle = TABLE.flipperRest; }
          else angle = lerp(TABLE.flipperUp, TABLE.flipperRest, u);
        }
      }
      const pose = this.batPose(i, angle, pivot);
      this.host.phys.moveKinematicPose(this.batBodies[i]!, this.w(pose.pos), pose.rot);
    }
  }

  private checkBumpers(t: TokenRuntime, local: Vec3, world: Vec3): void {
    for (const b of this.bumpers) {
      const bx = b.pos.x - this.origin.x;
      const by = b.pos.y - this.origin.y;
      const dx = local.x - bx;
      const dy = local.y - by;
      const d2 = dx * dx + dy * dy;
      const reach = b.r + t.radius + 0.08;
      if (d2 > reach * reach) continue;
      // One kick per approach. A token that comes to rest touching a bumper would
      // otherwise score every tick — the same shape of bug as the coin pusher's
      // per-coin avalanche, and the same fix: latch it. Instance state, never module
      // state, or twin runs see each other's cooldowns (MASCHINENBAU's trap).
      const key = t.id * 16 + this.bumpers.indexOf(b);
      if (this.host.tick - (this.bumperCooldown.get(key) ?? -999) < 14) continue;
      this.bumperCooldown.set(key, this.host.tick);
      const d = Math.sqrt(Math.max(d2, 1e-6));
      const imp = 3.2 + b.value * 0.12;
      this.host.phys.impulse(t.body, { x: (dx / d) * imp, y: (dy / d) * imp, z: 0 });
      // a bumper is a near-miss engine: it pays a little and feeds the pot a little
      this.feedTopf(1);
      this.registerScore(t, 'pilz', t.faceValue * b.value * 0.25, world, false);
    }
  }

  private checkTargets(t: TokenRuntime, local: Vec3, world: Vec3): void {
    for (let i = 0; i < this.targets.length; i++) {
      const tg = this.targets[i]!;
      if (tg.down) continue;
      const tx = tg.pos.x - this.origin.x;
      const ty = tg.pos.y - this.origin.y;
      if (Math.abs(local.x - tx) > tg.h.x + t.radius) continue;
      if (Math.abs(local.y - ty) > tg.h.y + t.radius) continue;

      // The Saalchef's GLÜCK: he buys one target back. Telegraphed when armed, and the
      // spend itself is an event — the player sees the rule that beat them.
      if (this.denyArmed) {
        this.denyArmed = false;
        this.host.emit({ t: 'glueckSpent', stream: 'flipper:saalchef', event: 'ziel' });
        this.host.phys.impulse(t.body, { x: 0, y: -2.5, z: 0 });
        return;
      }

      tg.down = true;
      this.registerScore(t, `ziel${i}`, t.faceValue * tg.value, world, false);
      if (this.targetsDown() === this.targets.length) {
        // a cleared bank is jackpot-class — this is what the pot is FOR
        this.targetsCleared++;
        this.registerScore(t, 'bankett', t.faceValue * 40 + this.topf, world, true);
        this.resetTargets();
      }
      return;
    }
  }

  private drain(t: TokenRuntime, local: Vec3): void {
    // [Gluecks] narrows the losing channel — the machine's one declared odds mapping
    // besides the cradle, and the same `pocketWidth` query the pachinko mouths use.
    const narrow = this.host.pipe.query('pocketWidth', ctxFor(this.id, this.host.tick, t), 1);
    const outlaneHalf = 4.5 / Math.max(0.2, narrow);
    const savedByLuck = Math.abs(local.x) > outlaneHalf;

    // A declared GLÜCK spend on the drain kicks the ball back into play, exactly once.
    // Not a roll: you either called the save before the ball went down or you did not.
    if (this.consumeBend('flipper', 'rettung')) {
      this.host.emit({ t: 'glueckSpent', stream: 'flipper', event: 'rettung' });
      this.host.phys.impulse(t.body, { x: local.x > 0 ? -2.5 : 2.5, y: 10, z: 0 });
      return;
    }
    // …and [Gluecks] narrowing the outlane can catch a ball that was only just lost.
    if (savedByLuck) {
      this.host.phys.impulse(t.body, { x: local.x > 0 ? -1.6 : 1.6, y: 6.5, z: 0 });
      return;
    }

    this.drained++;
    this.balls = Math.max(0, this.balls - 1);
    this.registerDud(t);
    this.unrake(t);
    this.host.removeToken(t.id, 'lost');
  }

  /** [Kleber] on the bat: the ball is held so you can aim. Released by flipping. */
  private releaseCradle(i: number): void {
    for (const [id, bat] of this.cradled) {
      if (bat !== i) continue;
      const t = this.host.tokensIn(this.id).find((x) => x.id === id);
      if (t && t.body >= 0) {
        this.host.phys.impulse(t.body, { x: i === 0 ? 3.4 : -3.4, y: 7.5, z: 0 });
      }
      this.cradled.delete(id);
    }
  }

  structuralLoad(): number {
    /**
     * A table's physical limit is how much is bouncing on it at once (machines.md: "table
     * max"). Value load is watched separately by the base class.
     *
     * It counts YOUR balls only. At first it counted every body on the table and the
     * Saalchef burst his own machine by playing: he adds one of his own coins every nine
     * seconds, and a fight that ran its full length reached the limit with the player
     * doing nothing at all. A structural limit the *antagonist* trips on his own is not a
     * limit, it is a timer. Multiball is what bursts a pinball table, and multiball is
     * something you choose (Nachlegen).
     */
    return this.playerBalls() / 10;
  }

  burstValue(): number {
    let v = this.topf;
    for (const t of this.host.tokensIn(this.id)) v += t.faceValue * 4;
    return v + 120 * this.stakeLevel + this.targetsCleared * 60;
  }

  extra(): unknown {
    return {
      targets: this.targets.map((t) => ({
        x: t.pos.x - this.origin.x, y: t.pos.y - this.origin.y, down: t.down,
      })),
      bumpers: this.bumpers.map((b) => ({ x: b.pos.x - this.origin.x, y: b.pos.y - this.origin.y, r: b.r })),
      flippers: [
        { side: 'left', angle: this.batAngle(0) },
        { side: 'right', angle: this.batAngle(1) },
      ],
      balls: this.ballsInPlay(),
      playerBalls: this.playerBalls(),
      houseBalls: this.houseTokens.size,
      drained: this.drained,
      cleared: this.targetsCleared,
      denyArmed: this.denyArmed,
      denyAt: this.denyTelegraphedAt,
      rake: RAKE_GRAVITY,
    };
  }

  private batAngle(i: number): number {
    if (this.flipT[i] < 0) return TABLE.flipperRest;
    const t = this.flipT[i];
    if (t < TABLE.flipTime) return lerp(TABLE.flipperRest, TABLE.flipperUp, t / TABLE.flipTime);
    if (t < TABLE.flipTime + TABLE.flipHold) return TABLE.flipperUp;
    const u = (t - TABLE.flipTime - TABLE.flipHold) / (TABLE.flipTime * 2.2);
    return lerp(TABLE.flipperUp, TABLE.flipperRest, Math.min(1, u));
  }
}

/** How many of the Saalchef's own coins may litter the table at once. */
const HOUSE_MAX = 3;

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
const lerp = (a: number, b: number, u: number): number => a + (b - a) * u;
